import React, { useState } from "react";
import { Zap, MapPin, Phone, LogIn, Lock, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { SHOP, bn } from "../lib/format";
import { CircuitBg } from "../components/Electric";

const HERO_IMG = "/login-hero.jpeg";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, pw);
    } catch (er) {
      setErr(errMsg(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-slate-50">
      {/* LEFT — brand / illustration panel */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0A1633] text-white p-10 xl:p-14">
        <CircuitBg />
        <div className="relative z-10 flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 grid place-items-center">
            <Zap className="text-emerald-300" fill="currentColor" size={22} />
          </span>
          <div className="leading-tight">
            <p className="font-bold text-lg">{SHOP.name}</p>
            <p className="text-[11px] tracking-[.3em] text-blue-300">{SHOP.nameEn}</p>
          </div>
        </div>

        <div className="relative z-10 flex-1 grid place-items-center py-6">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-400/20 blur-3xl rounded-full" />
            <img src={HERO_IMG} alt="রানা ইলেক্ট্রিক" className="relative w-[300px] xl:w-[360px] rounded-3xl shadow-2xl ring-1 ring-white/10" />
          </div>
        </div>

        <div className="relative z-10">
          <h2 className="text-3xl xl:text-4xl font-bold" data-testid="welcome-shop-name">{SHOP.name}</h2>
          <p className="text-blue-100/80 mt-2 max-w-md">দোকানের ডিজিটাল হিসাব — মেমো, বাকি, আদায়, স্টক আর ক্রয় এক জায়গায়।</p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm text-blue-100/80">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1"><MapPin size={14} /> {SHOP.address}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 num"><Phone size={14} /> {bn(SHOP.mobile)}</span>
          </div>
        </div>
      </section>

      {/* RIGHT — form */}
      <section className="relative flex items-center justify-center p-6 sm:p-10 min-h-screen">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <span className="w-11 h-11 rounded-xl bg-blue-700 grid place-items-center"><Zap className="text-amber-300" fill="currentColor" size={22} /></span>
            <div className="leading-tight">
              <p className="font-bold text-lg text-slate-900">{SHOP.name}</p>
              <p className="text-[11px] tracking-[.3em] text-blue-700">{SHOP.nameEn}</p>
            </div>
          </div>

          <span className="inline-grid w-12 h-12 rounded-2xl bg-blue-700 place-items-center mb-5 shadow-lg shadow-blue-700/30"><Lock size={22} className="text-white" /></span>
          <h1 className="text-3xl font-bold text-slate-900">স্বাগতম</h1>
          <p className="text-slate-500 mt-1 mb-7">মালিক লগইন — শুধুমাত্র দোকান মালিকের জন্য</p>

          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-600 mb-1">ইমেইল</span>
            <div className="relative">
              <Mail size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="inp pl-10" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoComplete="username" />
            </div>
          </label>
          <label className="block mb-5">
            <span className="block text-sm font-medium text-slate-600 mb-1">পাসওয়ার্ড</span>
            <div className="relative">
              <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="inp pl-10" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="login-password-input" autoComplete="current-password" />
            </div>
          </label>
          {err && <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">{err}</p>}
          <button className="btn btn-primary w-full" disabled={busy} data-testid="login-form-submit-button">
            <LogIn size={18} /> {busy ? "প্রবেশ করা হচ্ছে..." : "প্রবেশ করুন"}
          </button>
          <p className="text-center text-xs text-slate-400 mt-6">নিরাপদ লগইন • {SHOP.name}</p>
        </form>
      </section>
    </div>
  );
}
